import React, { useEffect, useRef } from 'react'
import { FundraisingChartStyled } from './style'
import * as echarts from 'echarts'
import useThemeVar from '@/hooks/useThemeStr'
import { useStore } from '@/store/index'
import useLangPrefix from '@/hooks/useLangPrefix'
import { useAdapter } from '@/components/Adapter'
import { createNoSelectedSVG, createSelectedSVG, pageIconGather } from '../TrendChart'
import type { FundraisingGraphChartData, FundraisingSeries } from 'api-fundraising'
import { observer } from 'mobx-react-lite'
type EChartsOption = echarts.EChartsOption

const color = ['#FECD4E', '#4DD8C1', '#F9B35F', '#5EAFFF', '#FE8870', '#4FC493', '#8764EE', '#6587EE', '#CB65EE']
const darkColor = ['#B88607', '#06917A', '#B36D18', '#1868B8', '#B8422A', '#087D4D', '#401EA7', '#1D57B8', '#851FA7']
const filterReg = /[-(){} &]/g

interface FundraisingChartProps {
  fundingData: FundraisingGraphChartData
}

const FundraisingChart: React.FC<FundraisingChartProps> = ({ fundingData }): React.ReactElement => {
  const { themeVar } = useThemeVar()
  const { globalStore } = useStore()
  const { isAr } = useLangPrefix()
  const { isH5 } = useAdapter()
  const chartDom = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (chartDom.current) {
      const myChart = echarts.init(chartDom.current)

      const nameMap = fundingData?.yData?.map((item: FundraisingSeries, index: number) => ({
        name: item?.name,
        id: 'name' + index,
      }))

      const series: echarts.BarSeriesOption[] = fundingData?.yData?.map((item: FundraisingSeries, index: number) => {
        return {
          name: nameMap[index]?.id,
          data: item?.counting ?? [],
          type: 'bar',
          stack: 'total',
          barWidth: '50%',
          smooth: true,
          showSymbol: false,
        }
      })

      const colorMap = globalStore.isDark ? darkColor : color

      const option: EChartsOption = {
        color: colorMap,
        tooltip: {
          trigger: 'axis',
          confine: true,
          extraCssText: 'z-index: 20',
          padding: [12, 12, 12, 12],
          borderWidth: 0,
          backgroundColor: themeVar('--color-cmpt-6'),
          textStyle: {
            color: themeVar('--color-text-6'),
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
          },
          formatter: function (_params: any) {
            let relVal = ``
            const params = _params?.reverse()

            for (let i = 0, l = params.length; i < l; i++) {
              const item = params[i]
              const label = nameMap.find((i: any) => i?.id === item.seriesName)?.name
              if (item?.data) {
                relVal += `
                  <div style="display: flex; align-items: center; gap: 60px; justify-content: space-between;">
                    <div style="display: flex; align-items: center; gap: 4px;">
                      <span style="display: inline-block; border-radius: 2px; width: 8px; height: 8px; background-color: ${
                        item?.color
                      };"></span>
                      <span style="font-size: 12px; font-weight: 500; color: #bfbfbf;">${label}</span>
                    </div>
                     <span style="font-size: 12px; font-weight: 500; color: #fafafa;">${item?.data}</span>
                  </div>
                `
              }
            }

            return `<div style="display: flex; flex-direction: column;gap: 8px;">
              <strong style="font-weight:500;font-size:12px;font-family:Gate_Sans;color:#fafafa;">${params[0]?.axisValue}</strong>
              ${relVal}
            </div>`
          },
        },
        legend: {
          type: 'scroll',
          bottom: 0,
          orient: 'horizontal',
          itemGap: 20,
          itemWidth: 0,
          itemHeight: 0,
          padding: [24, 0, 0],
          selectedMode: 'multiple',
          pageTextStyle: {
            color: themeVar('--color-text-3'),
          },
          pageIcons: {
            horizontal: [pageIconGather.left, pageIconGather.right],
          },
          pageIconColor: themeVar('--color-icon-1'),
          pageIconInactiveColor: themeVar('--color-icon-7'),
          pageIconSize: 12,
          formatter: name => {
            const selected = (myChart as any)?.getOption()?.legend[0]?.selected?.[name]
            const label = nameMap.find((item: any) => item?.id === name)?.name
            return `{${name?.replaceAll(filterReg, '') + `${selected ? '' : '_selected'}`}|}{name|${label}}`
          },
          textStyle: {
            rich: {
              name: {
                color: themeVar('--color-text-3'),
                fontSize: 12,
                fontWeight: 400,
                padding: [12, 0, 12, 4],
              },
              ...nameMap?.reduce((obj: any, { id }: any, index: number) => {
                obj[id?.replaceAll(filterReg, '') + '_selected'] = {
                  width: 12,
                  height: 12,
                  backgroundColor: {
                    image: createNoSelectedSVG({ fill: color[index] }),
                  },
                }
                return obj
              }, {}),
              ...nameMap?.reduce((obj: any, { id }: any, index: number) => {
                obj[id?.replaceAll(filterReg, '')] = {
                  width: 12,
                  height: 12,
                  backgroundColor: {
                    image: createSelectedSVG({ fill: color[index] }),
                  },
                }
                return obj
              }, {}),
            },
          },
          selected: nameMap?.reduce((obj: any, { id }: any) => {
            obj[id] = true
            return obj
          }, {}),
        },
        grid: {
          left: '32px',
          right: '16px',
          top: '16px',
          bottom: isH5 ? '80px' : '68px', // 增加 H5 下的底部间距
        },
        xAxis: {
          type: 'category',
          data: fundingData?.xData,
          boundaryGap: true,
          axisLine: {
            show: true,
            lineStyle: {
              color: themeVar('--color-divider-2'),
              width: 1,
            },
          },
          axisTick: {
            alignWithLabel: true,
            lineStyle: {
              width: 0,
            },
          },
          axisLabel: {
            interval: isH5 ? 1 : 0, // H5 下间隔显示标签
            rotate: isH5 ? 45 : 0, // H5 下旋转标签 45 度
            fontSize: 12,
            fontWeight: 400,
            fontFamily: 'Gate_Sans',
            color: themeVar('--color-text-3'),
            margin: 12,
            formatter: value =>
              `${value.split(' ')?.join(`
`)}`,
          },
        },
        yAxis: {
          type: 'value',
          boundaryGap: [0, '10%'],
          position: isAr ? 'right' : 'left',
          splitLine: {
            lineStyle: {
              color: themeVar('--color-divider-2'),
              width: 1,
              opacity: 0.7,
            },
          },
          axisLabel: {
            color: themeVar('--color-text-3'),
            fontSize: 12,
            fontWeight: 400,
            fontFamily: 'Gate_Sans',
          },
        },
        series,
        graphic: {
          type: 'image',
          z: -10,
          left: 'center',
          top: '30%',
          style: {
            image: '/price/public/images/icon-gt-logo.png',
            width: isH5 ? 90 : 180,
            height: isH5 ? 21 : 42,
            opacity: 0.8,
          },
        },
      }

      myChart.setOption(option)

      const resizeHandler = (e: any) => {
        if (e?.[0].contentRect?.width !== myChart?.getWidth()) {
          myChart.resize()
        }
      }

      const observer = new ResizeObserver(resizeHandler)

      if (chartDom.current) {
        observer.observe(chartDom.current)
      }

      window.addEventListener('resize', () => myChart.resize())

      return () => {
        window.removeEventListener('resize', () => myChart.resize())
        myChart && myChart.dispose()
        observer.disconnect()
      }
    }
  }, [fundingData, globalStore.isDark, isAr, isH5, themeVar])

  return (
    <FundraisingChartStyled>
      <div className="fundraising-chart" ref={chartDom}></div>
    </FundraisingChartStyled>
  )
}

export default observer(FundraisingChart)
